import { useEffect, useState, useRef, useMemo } from "react"
import { flushSync } from "react-dom"
import { Link, useNavigate } from "react-router-dom"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Button } from "@/modules/shared/ui/components/button"
import { Plus, Linkedin, Sparkles, Eye, Users, Edit, XCircle, Copy, CopyPlus, Trash2, Share2, Download } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/modules/shared/ui/components/tooltip"
import { SearchFilterBar } from "@/modules/shared/ui/components/custom/SearchFilterBar"
import {
  DataTable,
  TableColumn,
} from "@/modules/shared/ui/components/custom/CustomTable"
import { CreateVacancyDialog } from "../components/CreateVacancyDialog"
import { DeleteConfirmationDialog } from "../components/DeleteConfirmationDialog"
import {
  VacancyStatusParser,
  VacancyStatus,
} from "../../../enums/VacancyStatus"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { VacancyListItemDto } from "../../../dtos/VacancyListItemDto"
import { useVacancies } from "@/modules/recruitment/hooks/useVacancies"
import { AIVacancyDialog } from "../components/AIVacancyDialog"
import {
  AiGeneratedVacancyDto,
  vacancyService,
} from "@/modules/recruitment/infrastructure/vacancy-service"
import { toast } from "sonner"
import { SendRegretDialog } from "../components/SendRegretDialog"
import { Careers24ResultModal } from "../components/Careers24ResultModal"
import { DuplicateVacancyDialog } from "../components/DuplicateVacancyDialog"
import { Sage300VacanciesModal } from "../components/Sage300VacanciesModal"
import {
  getSage300Vacancies,
  generateVacancyFromSage300Position,
  Sage300Vacancy,
} from "@/modules/recruitment/infrastructure/sage300-service"
import { VacancyOrigin } from "@/modules/recruitment/dtos/VacancyDto"
import { useSelector } from "react-redux"
import { selectCurrentOrganization } from "@/modules/organizations/store/organizationSlice"
import { selectCareers24Connection } from "@/modules/profile/store/careers24ConnectorSlice"
import { HttpStatusCodes } from "@/lib/http-status-codes"

export default function VacancyPage() {
  type VacancyStatusFilter = VacancyStatus | "All"

  const vacancyStatusOptions: { label: string; value: VacancyStatusFilter }[] =
    [
      { label: "All", value: "All" },
      { label: "Active", value: VacancyStatus.Active },
      { label: "Closed", value: VacancyStatus.Closed },
      { label: "Expired", value: VacancyStatus.Expired },
    ]
  const navigate = useNavigate()
  const {
    vacancies,
    isLoading,
    isCareers24Loading,
    loadVacancies,
    createNewVacancy,
    updateVacancyStatusById,
    notifyRegretCandidates,
    deleteVacancyById,
    postVacancyToCareers24,
  } = useVacancies()
  const organization = useSelector(selectCurrentOrganization)
  const careers24Connection = useSelector(selectCareers24Connection)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<VacancyStatusFilter>("All")
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false)
  const [selectedVacancy, setSelectedVacancy] = useState<
    VacancyListItemDto | undefined
  >(undefined)
  const [showSendRegretDialog, setShowSendRegretDialog] = useState(false)
  const [recentlyClosedVacancy, setRecentlyClosedVacancy] =
    useState<VacancyListItemDto | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false)
  const [vacancyToDuplicate, setVacancyToDuplicate] =
    useState<VacancyListItemDto | null>(null)
  const [isContinuing, setIsContinuing] = useState(false)
  const [careers24Result, setCareers24Result] = useState<{
    success: boolean
    message?: string
    vacancyUrl?: string
  } | null>(null)
  const [isCareers24ModalOpen, setIsCareers24ModalOpen] = useState(false)
  const [isSage300ModalOpen, setIsSage300ModalOpen] = useState(false)
  const [sage300Vacancies, setSage300Vacancies] = useState<Sage300Vacancy[]>([])
  const [isSage300Loading, setIsSage300Loading] = useState(false)
  const [sage300Error, setSage300Error] = useState<string | null>(null)
  const [generatingSage300PositionId, setGeneratingSage300PositionId] =
    useState<number | null>(null)
  const isEditMode = useRef(false)
  const isMounted = useRef(false)

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      loadVacancies()
    }
  }, [loadVacancies])

  const filteredVacancies = useMemo(() => {
    return vacancies.filter((vacancy) => {
      const matchesSearch =
        !searchTerm ||
        vacancy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (vacancy.description ?? "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())

      const matchesStatus =
        statusFilter === "All" || vacancy.status === statusFilter

      return matchesSearch && matchesStatus
    })
  }, [vacancies, searchTerm, statusFilter])

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-700"
      case "published":
        return "bg-muted text-foreground"
      case "closed":
        return "bg-orange-100 text-orange-700"
      case "expired":
        return "bg-red-100 text-red-700"
      default:
        return "bg-muted text-foreground"
    }
  }
  const handleCloseVacancy = async (vacancy: VacancyListItemDto) => {
    try {
      await updateVacancyStatusById(vacancy.id, VacancyStatus.Closed)

      toast.success("Vacancy closed successfully")

      setRecentlyClosedVacancy(vacancy)
      setShowSendRegretDialog(true)
    } catch (error) {
      toast.error((error as Error)?.message ?? "Failed to close vacancy")
    }
  }

  const handleSendRegretMessages = async () => {
    if (!recentlyClosedVacancy) return

    try {
      const emailsSent = await notifyRegretCandidates(recentlyClosedVacancy.id)

      if (emailsSent === 0) {
        toast.info("No candidates to notify")
      } else {
        toast.success(`Regret messages sent to ${emailsSent} candidates`)
      }
    } catch (error: unknown) {
      toast.error((error as Error)?.message ?? "Failed to send regret messages")
    }
  }

  const handleDeleteVacancy = async () => {
    if (!selectedVacancy) return

    setIsDeleting(true)
    try {
      await deleteVacancyById(selectedVacancy.id)
      toast.success("Vacancy deleted successfully")
      setIsDeleteDialogOpen(false)
      setSelectedVacancy(undefined)
    } catch (error: unknown) {
      toast.error((error as Error)?.message || "Failed to delete vacancy")
    } finally {
      setIsDeleting(false)
    }
  }

  const handleShareToLinkedIn = (vacancy: VacancyListItemDto) => {
    const jd = vacancy

    const formatBulletList = (items?: string[]) =>
      items?.length ? items.map((item) => `- ${item}`).join("\n") : ""

    const formatCurrencyRange = (min?: number, max?: number) => {
      const hasRange = (min ?? 0) > 0 && (max ?? 0) > 0
      if (hasRange) {
        const formatter = new Intl.NumberFormat("en-ZA", {
          style: "currency",
          currency: "ZAR",
          maximumFractionDigits: 0,
        })
        return `${formatter.format(min as number)} - ${formatter.format(max as number)}`
      }
      return "Competitive"
    }

    const applyToken = btoa(`${vacancy.id}|${organization!.id}`)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "")
    const applyLink = import.meta.env.VITE_APPLY_LINK + applyToken

    const qualifications: string[] = []
    if (vacancy.yearsOfExperience && vacancy.yearsOfExperience > 0) {
      qualifications.push(`${vacancy.yearsOfExperience}+ years of experience`)
    }
    if (vacancy.educationLevel) {
      qualifications.push(vacancy.educationLevel)
    }
    if (vacancy.certifications?.length) {
      qualifications.push(
        ...vacancy.certifications.map((cert) =>
          typeof cert === "string" ? cert : cert.name,
        ),
      )
    }

    const shareSections = [
      `${vacancy.department ? `${vacancy.department} is` : "We are"} looking for a ${vacancy.title} to join our team.`,
      "",
      vacancy.locationType ? `Location: ${vacancy.locationType}` : "",
      vacancy.jobType
        ? `Employment Type: ${vacancy.jobType.replace(/([a-z])([A-Z])/g, "$1 $2")}`
        : "",
      `Salary: ${formatCurrencyRange(vacancy.minimumSalary, vacancy.maximumSalary)}`,
      "",
      jd?.description ? `ABOUT THE ROLE\n${jd.description}` : "",
      "",
      jd?.keyResponsibilities?.length
        ? `KEY RESPONSIBILITIES\n${formatBulletList(jd.keyResponsibilities.map((kr) => kr.name))}`
        : "",
      "",
      jd?.requiredSkills?.length
        ? `REQUIRED SKILLS\n${formatBulletList(jd.requiredSkills.map((rs) => rs.name))}`
        : "",
      "",
      qualifications.length
        ? `QUALIFICATIONS\n${formatBulletList(qualifications)}`
        : "",
      "",
      jd?.optionalSkills?.length
        ? `NICE TO HAVE\n${formatBulletList(jd.optionalSkills.map((os) => os.name))}`
        : "",
      "",
      `HOW TO APPLY\nApply here: ${applyLink}`,
      "",
      `Join us in building the future. We offer competitive compensation, flexible working, and opportunities for growth.`,
      "",
      `#Hiring #JobOpportunity #Careers`,
    ]

    const shareText = shareSections.filter((s) => s).join("\n\n")

    const useLinkedInPreview =
      import.meta.env.VITE_LINKEDIN_SHARE_PREVIEW === "true"

    if (useLinkedInPreview) {
      navigate(`/linkedin-share-preview?text=${encodeURIComponent(shareText)}`)
    } else {
      const linkedinUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(shareText)}`
      window.open(linkedinUrl, "_blank")
    }
  }

  const handleShareToCareers24 = async (vacancy: VacancyListItemDto) => {
    flushSync(() => {
      setCareers24Result(null)
      setIsCareers24ModalOpen(true)
    })
    try {
      const result = await postVacancyToCareers24(vacancy.id)
      setCareers24Result(result)
    } catch (error: unknown) {
      setIsCareers24ModalOpen(false)
      toast.error(
        (error as Error)?.message ?? "Failed to post vacancy to Careers24",
      )
    }
  }

  const handleAIGenerate = async (prompt: string) => {
    try {
      if (!organization?.id) {
        toast.error("Organization not found")
        return
      }
      const generatedVacancy = await vacancyService.generateVacancyWithAI(
        prompt,
        organization.id,
      )
      const newVacancy = {
        title: generatedVacancy.title,
        description: generatedVacancy.description,
        locationType: generatedVacancy.locationType,
        jobType: generatedVacancy.jobType,
        department: generatedVacancy.department,
        keyResponsibilities: generatedVacancy.keyResponsibilities,
        requiredSkills: generatedVacancy.requiredSkills,
        optionalSkills: generatedVacancy.optionalSkills,
        educationLevel: generatedVacancy.educationLevel || "None",
        yearsOfExperience: generatedVacancy.yearsOfExperience || 0,
        certifications: generatedVacancy.certifications,
        seniorityLevel: generatedVacancy.seniorityLevel,
        minimumSalary: generatedVacancy.minimumSalary || 0,
        maximumSalary: generatedVacancy.maximumSalary || 0,
      } as AiGeneratedVacancyDto
      setSelectedVacancy(newVacancy as unknown as VacancyListItemDto)
      setIsCreateDialogOpen(true)
    } catch (error) {
      if (error instanceof Error && error.name === "InsufficientFundsError") {
        throw error
      }
      const responseMessage = (
        error as { response?: { data?: { message?: string; error?: string } } }
      )?.response?.data
      const message =
        responseMessage?.message ??
        responseMessage?.error ??
        (error instanceof Error ? error.message : null) ??
        "Failed to generate vacancy. Please try again."
      throw new Error(message)
    }
  }

  const handleImportFromSage300 = async () => {
    if (!organization?.id) return
    setSage300Vacancies([])
    setSage300Error(null)
    setIsSage300ModalOpen(true)
    setIsSage300Loading(true)
    try {
      const data = await getSage300Vacancies(organization.id)
      setSage300Vacancies(data)
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status
      if (status === HttpStatusCodes.NOT_FOUND) {
        setSage300Error(
          "No Sage 300 connection is configured for this organisation.",
        )
      } else {
        setSage300Error("Failed to load Sage 300 vacancies. Please try again.")
      }
    } finally {
      setIsSage300Loading(false)
    }
  }

  const handleGenerateFromSage300 = async (positionId: number) => {
    if (!organization?.id) return
    setGeneratingSage300PositionId(positionId)
    try {
      const generated = await generateVacancyFromSage300Position(
        organization.id,
        positionId,
      )
      const newVacancy = {
        title: generated.title,
        description: generated.description,
        locationType: generated.locationType,
        jobType: generated.jobType,
        department: generated.department,
        keyResponsibilities: generated.keyResponsibilities,
        requiredSkills: generated.requiredSkills,
        optionalSkills: generated.optionalSkills,
        educationLevel: generated.educationLevel || "None",
        yearsOfExperience: generated.yearsOfExperience || 0,
        certifications: generated.certifications,
        seniorityLevel: generated.seniorityLevel,
        minimumSalary: generated.minimumSalary || 0,
        maximumSalary: generated.maximumSalary || 0,
        sageMetadata: generated.sageMetadata ?? null,
      } as AiGeneratedVacancyDto
      setIsSage300ModalOpen(false)
      setSage300Vacancies([])
      setSage300Error(null)
      setSelectedVacancy(newVacancy as unknown as VacancyListItemDto)
      setIsCreateDialogOpen(true)
    } catch (error: unknown) {
      const errMsg = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message
      toast.error(errMsg ?? "Failed to generate vacancy from Sage 300.")
    } finally {
      setGeneratingSage300PositionId(null)
    }
  }

  const handleDuplicateVacancy = async (vacancy: VacancyListItemDto) => {
    try {
      await createNewVacancy({
        title: `${vacancy.title}`,
        description: vacancy.description ?? "",
        jobType: vacancy.jobType,
        department: vacancy.department,
        locationType: vacancy.locationType,
        seniorityLevel: vacancy.seniorityLevel,
        minimumSalary: vacancy.minimumSalary,
        maximumSalary: vacancy.maximumSalary,
        yearsOfExperience: vacancy.yearsOfExperience ?? 0,
        educationLevel: vacancy.educationLevel ?? "",
        keyResponsibilities: vacancy.keyResponsibilities ?? [],
        requiredSkills: vacancy.requiredSkills ?? [],
        optionalSkills: vacancy.optionalSkills ?? [],
        certifications: vacancy.certifications ?? [],
        applicationDeadline: vacancy.applicationDeadline,
        baseScore: vacancy.baseScore ?? 0,
        organizationId: vacancy.organizationId || "",
        status: VacancyStatusParser(vacancy.status),
      })
      toast.success("Vacancy duplicated successfully")
    } catch (error: unknown) {
      toast.error((error as Error)?.message ?? "Failed to duplicate vacancy")
    }
  }

  const columns: TableColumn<VacancyListItemDto>[] = [
    {
      key: "title",
      label: "Title",
      headerClassName: "w-[30%]",
      render: (vacancy) => (
        <div className="flex flex-col min-w-0 max-w-md">
          <div className="flex items-center gap-2 min-w-0">
            <Link
              to={`/recruitment/vacancies/${vacancy.id}`}
              className="font-medium text-blue-700 hover:text-blue-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-sm truncate"
            >
              {vacancy.title}
            </Link>
            {vacancy.origin === VacancyOrigin.Sage300People && (
              <Badge className="shrink-0 bg-indigo-100 text-indigo-700 rounded-sm text-[10px] px-1.5 py-0">
                Sage 300
              </Badge>
            )}
          </div>
          <span className="text-xs text-gray-500 truncate">
            {vacancy.description}
          </span>
        </div>
      ),
    },
    {
      key: "applicantsCount",
      label: "Number of applicants",
      headerClassName: "text-center",
      render: (vacancy) => (
        <div className="text-center">
          <Badge>{vacancy.applicantsCount ?? 0}</Badge>
        </div>
      ),
    },
    {
      key: "createdAt",
      label: "Job listing date",
      headerClassName: "text-center",
      render: (vacancy) => (
        <div className="text-center">
          <span className="text-muted-foreground">
            {new Date(vacancy.createdAt).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>
      ),
    },
    {
      key: "applicationDeadline",
      label: "Application Deadline",
      headerClassName: "text-center",
      render: (vacancy) => (
        <div className="text-center">
          <span className="text-muted-foreground">
            {vacancy.applicationDeadline
              ? new Date(vacancy.applicationDeadline).toLocaleDateString(
                  undefined,
                  {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  },
                )
              : "—"}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      headerClassName: "text-center",
      render: (vacancy) => (
        <div className="text-center">
          <Badge className={`${getStatusColor(vacancy.status)} rounded-sm`}>
            {vacancy.status}
          </Badge>
        </div>
      ),
    },
    {
      key: "id",
      label: "Actions",
      headerClassName: "text-center w-[120px]",
      cellClassName: "!px-3",
      render: (vacancy) => {
        const isClosed = vacancy.status === VacancyStatus.Closed
        const careers24Posting = vacancy.externalPostings?.find(
          (p) => p.platform === "Careers24",
        )
        const iconActions = [
          {
            icon: Eye,
            label: "View Details",
            description: "Open the full vacancy details page",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              navigate(`/recruitment/vacancies/${vacancy.id}`)
            },
            disabled: false,
            color: "text-primary",
          },
          {
            icon: Copy,
            label: "Copy Application Link",
            description: "Copy the direct application link",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              if (!import.meta.env.VITE_APPLY_LINK) {
                toast.error("Something went wrong, please try again later")
                return
              }
              const token = btoa(`${vacancy.id}|${organization!.id}`)
                .replace(/\+/g, "-")
                .replace(/\//g, "_")
                .replace(/=/g, "")
              navigator.clipboard.writeText(
                import.meta.env.VITE_APPLY_LINK + token,
              )
              toast.success("Application link copied!")
            },
            disabled: false,
            color: "text-indigo-500",
          },
          {
            icon: Linkedin,
            label: "Share to LinkedIn",
            description: "Post this vacancy on LinkedIn",
            onClick: (e: React.MouseEvent) => { e.stopPropagation(); handleShareToLinkedIn(vacancy) },
            disabled: isClosed,
            color: "text-sky-600",
          },
          {
            icon: Users,
            label: "View Applications",
            description: "See all applicants for this vacancy",
            onClick: (e: React.MouseEvent) => { e.stopPropagation(); navigate(`/recruitment/vacancies/${vacancy.id}/applicants`, { state: { vacancyTitle: vacancy.title, baseScore: vacancy.baseScore } }) },
            disabled: isClosed,
            color: "text-violet-500",
          },
          {
            icon: Edit,
            label: "Edit Vacancy",
            description: "Modify the vacancy details",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              setSelectedVacancy(vacancy)
              setIsCreateDialogOpen(true)
              isEditMode.current = true
            },
            disabled: isClosed,
            color: "text-amber-500",
          },
          {
            icon: XCircle,
            label: "Close Vacancy",
            description: "Mark this vacancy as closed",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              handleCloseVacancy(vacancy)
            },
            disabled: isClosed,
            color: "text-orange-500",
          },
          {
            icon: CopyPlus,
            label: "Duplicate Vacancy",
            description: "Create a copy of this vacancy",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              setVacancyToDuplicate(vacancy)
              setIsDuplicateDialogOpen(true)
            },
            disabled: false,
            color: "text-muted-foreground",
          },
          {
            icon: Trash2,
            label: "Delete Vacancy",
            description: "Permanently remove this vacancy",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              setSelectedVacancy(vacancy)
              setIsDeleteDialogOpen(true)
            },
            disabled: false,
            color: "text-destructive",
          },
        ]
        return (
          <TooltipProvider>
            <div className="grid grid-cols-3 gap-1 justify-items-center w-fit mx-auto">
              {iconActions.map((action, idx) => {
                const Icon = action.icon
                return (
                  <Tooltip key={idx}>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={action.onClick}
                        disabled={action.disabled}
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${action.disabled
                            ? "bg-gray-50 border border-gray-100 opacity-40 cursor-not-allowed"
                            : "bg-gray-100 border border-gray-200 hover:scale-110 hover:bg-white hover:border-gray-300 cursor-pointer"
                          }`}
                      >
                        <Icon className={`w-3.5 h-3.5 ${action.disabled ? "text-gray-300" : action.color}`} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="max-w-[200px] p-0 overflow-hidden"
                    >
                      <div className="px-3.5 py-2.5 space-y-1">
                        <div className="flex items-center gap-2">
                          <Icon
                            className={`w-3.5 h-3.5 shrink-0 ${action.color}`}
                          />
                          <span className="text-[13px] font-semibold text-white leading-tight">
                            {action.label}
                          </span>
                        </div>
                        <p className="text-[11px] leading-relaxed text-gray-400">{action.description}</p>
                        {action.disabled && (
                          <div className="inline-flex items-center gap-1 mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-medium bg-white/10 text-muted-foreground">
                            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                            Not available for closed vacancies
                          </div>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                )
              })}
              {careers24Connection && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation()
                        if (careers24Posting) {
                          if (careers24Posting.postingUrl) {
                            navigator.clipboard.writeText(
                              careers24Posting.postingUrl,
                            )
                            toast.success("Careers24 link copied!")
                          } else {
                            toast.info(
                              "Your vacancy is shared, please check on Careers24",
                            )
                          }
                        } else if (!isClosed) {
                          handleShareToCareers24(vacancy)
                        }
                      }}
                      disabled={!careers24Posting && isClosed}
                      className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${careers24Posting
                          ? "bg-teal-50 border-2 border-teal-400 hover:scale-110 hover:bg-teal-100 cursor-pointer ring-1 ring-teal-300"
                          : isClosed
                            ? "bg-gray-50 border border-gray-100 opacity-40 cursor-not-allowed"
                            : "bg-gray-100 border border-gray-200 hover:scale-110 hover:bg-white hover:border-gray-300 cursor-pointer"
                        }`}
                    >
                      <Share2 className={`w-3.5 h-3.5 ${careers24Posting ? "text-teal-600" : isClosed ? "text-gray-300" : "text-teal-500"}`} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[260px] p-0 overflow-hidden"
                  >
                    <div className="px-3.5 py-2.5 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Share2
                          className={`w-3.5 h-3.5 shrink-0 ${careers24Posting ? "text-teal-400" : "text-teal-500"}`}
                        />
                        <span className="text-[13px] font-semibold text-white leading-tight">
                          {careers24Posting
                            ? "Posted on Careers24"
                            : "Share to Careers24"}
                        </span>
                      </div>
                      {careers24Posting ? (
                        <>
                          <div className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium bg-teal-500/20 text-teal-300">
                            <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                            Live since{" "}
                            {new Date(
                              careers24Posting.postedAt,
                            ).toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </div>
                          <p className="text-[11px] leading-relaxed text-gray-400 truncate max-w-[220px]">{careers24Posting.postingUrl}</p>
                          <div className="flex items-center gap-1.5 pt-0.5">
                            <Copy className="w-3 h-3 text-gray-400" />
                            <span className="text-[11px] text-gray-400">Click to copy link</span>
                          </div>
                        </>
                      ) : (
                        <p className="text-[11px] leading-relaxed text-gray-400">Post this vacancy to Careers24</p>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        )
      },
    },
  ]

  return (
    <div className="p-4 space-y-6">
      <div className="bg-card rounded-lg border border-border p-8 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-foreground">Vacancies</h1>
          <div className="flex items-center gap-3">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={() => {
                setSelectedVacancy(undefined)
                setIsCreateDialogOpen(true)
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create new vacancy
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3"
              onClick={() => {
                setIsAIDialogOpen(true)
              }}
            >
              <Sparkles className="h-4 w-4" />
              Create with AI
            </Button>
            <Button
              variant="outline"
              onClick={handleImportFromSage300}
              disabled={isSage300Loading}
            >
              {isSage300Loading ? (
                <>
                  <Download className="h-4 w-4 mr-2 animate-pulse" />
                  Importing...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Import from Sage 300
                </>
              )}
            </Button>
          </div>
        </div>

        <SearchFilterBar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search vacancies..."
          filterValue={statusFilter}
          onFilterChange={(value) =>
            setStatusFilter(value as VacancyStatusFilter)
          }
          filterOptions={vacancyStatusOptions}
          filterPlaceholder="Status"
          count={filteredVacancies.length}
          countLabel="Vacancies"
          hasData={vacancies.length > 0}
          defaultFilterValue="All"
        />
      </div>

      {/* Vacancies Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner className="size-6" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          onRowClick={(vacancy) => {
            navigate(`/recruitment/vacancies/${vacancy.id}/applicants`, {
              state: {
                vacancyTitle: vacancy.title,
                baseScore: vacancy.baseScore,
              },
            })
          }}
          data={[...filteredVacancies].sort((a, b) => {
            const statusOrder = {
              [VacancyStatus.Active]: 0,
              [VacancyStatus.Closed]: 1,
              [VacancyStatus.Expired]: 2,
            }
            return (
              (statusOrder[a.status as VacancyStatus] ?? 3) -
              (statusOrder[b.status as VacancyStatus] ?? 3)
            )
          })}
          emptyMessage={
            searchTerm
              ? "No vacancies found matching your search."
              : "No vacancies available."
          }
        />
      )}

      <AIVacancyDialog
        isOpen={isAIDialogOpen}
        setIsOpen={setIsAIDialogOpen}
        onGenerate={handleAIGenerate}
      />

      <CreateVacancyDialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open)
          if (!open) {
            setSelectedVacancy(undefined)
            isEditMode.current = false
          }
        }}
        isEditMode={isEditMode.current}
        vacancy={selectedVacancy}
      />

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsDeleteDialogOpen(open)
          if (!open) setSelectedVacancy(undefined)
        }}
        onConfirm={handleDeleteVacancy}
        title="Delete Vacancy"
        description={`Are you sure you want to delete "${selectedVacancy?.title}"? This action cannot be undone.`}
        isDeleting={isDeleting}
      />
      <SendRegretDialog
        open={showSendRegretDialog}
        onOpenChange={setShowSendRegretDialog}
        onConfirm={handleSendRegretMessages}
        vacancyTitle={recentlyClosedVacancy?.title}
      />

      <Careers24ResultModal
        open={isCareers24ModalOpen}
        onOpenChange={(open) => {
          setIsCareers24ModalOpen(open)
          if (!open) setCareers24Result(null)
        }}
        result={careers24Result}
        isLoading={isCareers24Loading}
      />

      <Sage300VacanciesModal
        open={isSage300ModalOpen}
        onOpenChange={(open) => {
          setIsSage300ModalOpen(open)
          if (!open) {
            setSage300Vacancies([])
            setSage300Error(null)
          }
        }}
        vacancies={sage300Vacancies}
        isLoading={isSage300Loading}
        error={sage300Error}
        onGenerateAI={handleGenerateFromSage300}
        generatingPositionId={generatingSage300PositionId}
      />

      <DuplicateVacancyDialog
        open={isDuplicateDialogOpen}
        onOpenChange={(open) => {
          setIsDuplicateDialogOpen(open)
          if (!open) setVacancyToDuplicate(null)
        }}
        vacancyTitle={vacancyToDuplicate?.title}
        isContinuing={isContinuing}
        onEdit={() => {
          setIsDuplicateDialogOpen(false)
          setSelectedVacancy(vacancyToDuplicate ?? undefined)
          isEditMode.current = false
          setIsCreateDialogOpen(true)
        }}
        onContinue={async () => {
          if (!vacancyToDuplicate) return
          setIsContinuing(true)
          try {
            await handleDuplicateVacancy(vacancyToDuplicate)
          } finally {
            setIsContinuing(false)
            setIsDuplicateDialogOpen(false)
            setVacancyToDuplicate(null)
          }
        }}
      />
    </div>
  )
}